<template>
  <div class="help-repay-management">
    <div class="header">
      <h2>亲友帮还记录管理</h2>
      <div class="actions">
        <el-button type="primary" @click="handleCreateHelpRepay">
          <el-icon><Plus /></el-icon>
          创建帮还记录
        </el-button>
        <el-button @click="refreshData">
          <el-icon><Refresh /></el-icon>
          刷新
        </el-button>
      </div>
    </div>

    <!-- 搜索和筛选 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="searchForm">
        <el-form-item label="搜索">
          <el-input v-model="searchForm.search" placeholder="用户姓名/手机号" clearable />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="待处理" value="pending" />
            <el-option label="已接受" value="accepted" />
            <el-option label="已拒绝" value="rejected" />
            <el-option label="已过期" value="expired" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">
            <el-icon><Search /></el-icon>
            搜索
          </el-button>
          <el-button @click="resetSearch">
            <el-icon><Refresh /></el-icon>
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 帮还记录列表 -->
    <el-card>
      <el-table :data="helpRepayList" v-loading="loading" stripe border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="发起用户" width="180">
          <template #default="{ row }">
            <div v-if="row.fromUser" class="user-info">
              <div class="user-name">{{ row.fromUser.realName || '未设置' }}</div>
              <div class="user-mobile">{{ row.fromUser.mobile }}</div>
            </div>
            <div v-else>用户{{ row.fromUserId }}</div>
          </template>
        </el-table-column>
        <el-table-column label="目标用户" width="180">
          <template #default="{ row }">
            <div v-if="row.toUser" class="user-info">
              <div class="user-name">{{ row.toUser.realName || '未设置' }}</div>
              <div class="user-mobile">{{ row.toUser.mobile }}</div>
            </div>
            <div v-else>用户{{ row.toUserId }}</div>
          </template>
        </el-table-column>
        <el-table-column label="商户" width="150">
          <template #default="{ row }">
            <div v-if="row.merchant" class="merchant-info">
              <div class="merchant-name">{{ row.merchant.name || `商户${row.merchantId}` }}</div>
              <div class="commission-rate">佣金率: {{ ((row.merchant.commissionRate || 0.1) * 100).toFixed(1) }}%</div>
            </div>
            <div v-else>商户{{ row.merchantId }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="消费金额" width="120">
          <template #default="{ row }">
            <span class="amount">¥{{ formatAmount(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusTag(row.status)">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="描述" min-width="150">
          <template #default="{ row }">
            {{ row.description || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column prop="acceptedAt" label="接受时间" width="180">
          <template #default="{ row }">
            {{ row.acceptedAt ? formatDate(row.acceptedAt) : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetails(row)">
              详情
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="success" 
              size="small" 
              @click="handleApprove(row)"
            >
              通过
            </el-button>
            <el-button 
              v-if="row.status === 'pending'" 
              type="danger" 
              size="small" 
              @click="handleReject(row)"
            >
              拒绝
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          v-model:page-size="pagination.size"
          :total="pagination.total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="帮还记录详情"
      width="700px"
    >
      <div v-if="currentRecord" class="record-detail">
        <el-descriptions title="基本信息" :column="2" border>
          <el-descriptions-item label="记录ID">{{ currentRecord.id }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTag(currentRecord.status)">
              {{ getStatusText(currentRecord.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="发起用户">
            <span v-if="currentRecord.fromUser">
              {{ currentRecord.fromUser.realName || '未设置' }} ({{ currentRecord.fromUser.mobile }})
            </span>
            <span v-else>用户{{ currentRecord.fromUserId }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="目标用户">
            <span v-if="currentRecord.toUser">
              {{ currentRecord.toUser.realName || '未设置' }} ({{ currentRecord.toUser.mobile }})
            </span>
            <span v-else>用户{{ currentRecord.toUserId }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="商户">
            <span v-if="currentRecord.merchant">
              {{ currentRecord.merchant.name }} (佣金率: {{ ((currentRecord.merchant.commissionRate || 0.1) * 100).toFixed(1) }}%)
            </span>
            <span v-else>商户{{ currentRecord.merchantId }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="消费金额">
            ¥{{ formatAmount(currentRecord.amount) }}
          </el-descriptions-item>
          <el-descriptions-item label="描述" :span="2">
            {{ currentRecord.description || '无描述' }}
          </el-descriptions-item>
          <el-descriptions-item label="创建时间">
            {{ formatDate(currentRecord.createdAt) }}
          </el-descriptions-item>
          <el-descriptions-item label="接受时间">
            {{ currentRecord.acceptedAt ? formatDate(currentRecord.acceptedAt) : '未接受' }}
          </el-descriptions-item>
          <el-descriptions-item v-if="currentRecord.rejectedAt" label="拒绝时间">
            {{ formatDate(currentRecord.rejectedAt) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="currentRecord.failedReason" label="失败原因">
            <el-tag type="danger">{{ currentRecord.failedReason }}</el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <div class="business-logic-note mt-20">
          <el-alert
            title="业务逻辑说明"
            type="info"
            :closable="false"
          >
            <template #default>
              <p>亲友团在签约商户消费后，商户返佣给平台，佣金分成规则：</p>
              <ul>
                <li>公司利润：总佣金的30%</li>
                <li>用户分成：总佣金的70%</li>
                <li>用户分成中：解债分（债务人）70%，解债分（债权人）30%</li>
              </ul>
            </template>
          </el-alert>
        </div>
      </div>
    </el-dialog>

    <!-- 创建帮还记录对话框 -->
    <el-dialog
      v-model="createDialogVisible"
      title="创建帮还记录"
      width="500px"
    >
      <el-form 
        ref="helpRepayFormRef" 
        :model="helpRepayForm" 
        :rules="helpRepayRules" 
        label-width="100px"
      >
        <el-form-item label="发起用户ID" prop="fromUserId">
          <el-input v-model="helpRepayForm.fromUserId" placeholder="请输入亲友用户ID" />
        </el-form-item>
        <el-form-item label="目标用户ID" prop="toUserId">
          <el-input v-model="helpRepayForm.toUserId" placeholder="请输入债务人用户ID" />
        </el-form-item>
        <el-form-item label="商户ID" prop="merchantId">
          <el-input v-model="helpRepayForm.merchantId" placeholder="请输入商户ID" />
        </el-form-item>
        <el-form-item label="消费金额" prop="amount">
          <el-input v-model="helpRepayForm.amount" placeholder="请输入消费金额">
            <template #prepend>¥</template>
          </el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input
            v-model="helpRepayForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入帮还描述"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="createDialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="handleSubmitHelpRepay">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, 
  Refresh, 
  Search
} from '@element-plus/icons-vue'
import adminService from '@/api/admin'

// 状态管理
const loading = ref(false)
const detailDialogVisible = ref(false)
const createDialogVisible = ref(false)
const submitting = ref(false)
const helpRepayFormRef = ref()
const currentRecord = ref(null)

// 搜索表单
const searchForm = reactive({
  search: '',
  status: ''
})

// 帮还记录表单
const helpRepayForm = reactive({
  fromUserId: '',
  toUserId: '',
  merchantId: '',
  amount: '',
  description: ''
})

// 表单验证规则
const helpRepayRules = {
  fromUserId: [
    { required: true, message: '请输入亲友用户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '请输入有效的用户ID', trigger: 'blur' }
  ],
  toUserId: [
    { required: true, message: '请输入债务人用户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '请输入有效的用户ID', trigger: 'blur' }
  ],
  merchantId: [
    { required: true, message: '请输入商户ID', trigger: 'blur' },
    { pattern: /^\d+$/, message: '请输入有效的商户ID', trigger: 'blur' }
  ],
  amount: [
    { required: true, message: '请输入消费金额', trigger: 'blur' },
    { pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效的金额，最多两位小数', trigger: 'blur' }
  ]
}

// 分页配置
const pagination = reactive({
  current: 1,
  size: 10,
  total: 0
})

// 帮还记录列表
const helpRepayList = ref([])

// 状态标签映射
const statusTagMap = {
  pending: 'warning',
  accepted: 'success',
  rejected: 'danger',
  expired: 'info',
  failed: 'danger'
}

// 状态文本映射
const statusTextMap = {
  pending: '待处理',
  accepted: '已接受',
  rejected: '已拒绝',
  expired: '已过期',
  failed: '失败'
}

// 获取状态标签
const getStatusTag = (status) => {
  return statusTagMap[status] || 'info'
}

// 获取状态文本
const getStatusText = (status) => {
  return statusTextMap[status] || status
}

// 格式化金额
const formatAmount = (amount) => {
  if (!amount) return '0.00'
  return parseFloat(amount).toFixed(2)
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

// 加载帮还记录数据
const loadHelpRepayData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.current,
      limit: pagination.size
    }
    
    if (searchForm.search) params.search = searchForm.search
    if (searchForm.status) params.status = searchForm.status
    
    const response = await adminService.get('/friend-groups/admin/help-repay-requests', { params })
    
    if (response) {
      helpRepayList.value = response.data || []
      pagination.total = response.total || 0
    }
  } catch (error) {
    console.error('加载帮还记录失败:', error)
    ElMessage.error('加载数据失败')
  } finally {
    loading.value = false
  }
}

// 搜索
const handleSearch = () => {
  pagination.current = 1
  loadHelpRepayData()
}

// 重置搜索
const resetSearch = () => {
  searchForm.search = ''
  searchForm.status = ''
  pagination.current = 1
  loadHelpRepayData()
}

// 刷新数据
const refreshData = () => {
  loadHelpRepayData()
}

// 查看详情
const handleViewDetails = (record) => {
  currentRecord.value = record
  detailDialogVisible.value = true
}

// 通过帮还请求
const handleApprove = async (record) => {
  try {
    await ElMessageBox.confirm(
      '确定要通过此帮还请求吗？通过后系统将自动处理佣金分成和积分分配。',
      '确认通过',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    // 使用POST请求接受帮还
    await adminService.post(`/friend-groups/help-repay-request/${record.id}/accept`)
    ElMessage.success('帮还请求已通过')
    loadHelpRepayData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('操作失败:', error)
      ElMessage.error(error.response?.data?.message || '操作失败')
    }
  }
}

// 拒绝帮还请求
const handleReject = async (record) => {
  try {
    await ElMessageBox.confirm(
      '确定要拒绝此帮还请求吗？',
      '确认拒绝',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    await adminService.post(`/friend-groups/help-repay-request/${record.id}/reject`)
    ElMessage.success('帮还请求已拒绝')
    loadHelpRepayData()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('操作失败:', error)
      ElMessage.error(error.response?.data?.message || '操作失败')
    }
  }
}

// 创建帮还记录
const handleCreateHelpRepay = () => {
  Object.keys(helpRepayForm).forEach(key => {
    helpRepayForm[key] = ''
  })
  createDialogVisible.value = true
}

// 提交帮还记录
const handleSubmitHelpRepay = async () => {
  if (!helpRepayFormRef.value) return
  
  try {
    await helpRepayFormRef.value.validate()
    
    submitting.value = true
    
    const data = {
      fromUserId: parseInt(helpRepayForm.fromUserId),
      toUserId: parseInt(helpRepayForm.toUserId),
      merchantId: parseInt(helpRepayForm.merchantId),
      consumptionAmount: parseFloat(helpRepayForm.amount),
      description: helpRepayForm.description
    }
    
    // 直接调用help-repay执行帮还
    await adminService.post('/friend-groups/help-repay', data)
    ElMessage.success('帮还记录创建成功')
    createDialogVisible.value = false
    loadHelpRepayData()
  } catch (error) {
    console.error('创建帮还记录失败:', error)
    ElMessage.error(error.response?.data?.message || '创建失败')
  } finally {
    submitting.value = false
  }
}

// 分页大小改变
const handleSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  loadHelpRepayData()
}

// 当前页改变
const handleCurrentChange = (page) => {
  pagination.current = page
  loadHelpRepayData()
}

// 组件挂载时加载数据
onMounted(() => {
  loadHelpRepayData()
})
</script>

<style scoped>
.help-repay-management {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.user-info, .merchant-info {
  line-height: 1.4;
}

.user-name, .merchant-name {
  font-weight: 500;
}

.user-mobile, .commission-rate {
  font-size: 12px;
  color: #666;
}

.amount {
  font-weight: 600;
  color: #409eff;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.record-detail {
  max-height: 500px;
  overflow-y: auto;
}

.mt-20 {
  margin-top: 20px;
}

.business-logic-note ul {
  margin: 10px 0 0 20px;
  padding: 0;
}

.business-logic-note li {
  margin: 5px 0;
}
</style>
